import React, { useCallback, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import { useAudio } from '../contexts/AudioContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faSearchPlus, faSearchMinus } from '@fortawesome/free-solid-svg-icons';
import './css/AudioEditor.css';

const DEFAULT_ZOOM_LEVEL = 0;
const REGION_DURATION = 60; // 60 seconds fixed duration

const AudioEditor = () => {
  const { audioData, updatedRegion, onUpdateRegion } = useAudio();
  
  // State
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM_LEVEL);
  const [region, setRegion] = useState(updatedRegion || { start: 0, end: REGION_DURATION });
  
  // Refs
  const wavesurferRef = useRef(null);
  const containerRef = useRef(null);
  const regionElementRef = useRef(null);
  const onUpdateRegionRef = useRef(onUpdateRegion);
  const updatedRegionRef = useRef(updatedRegion);
  const isPlayingRef = useRef(isPlaying);
  
  // Keep the refs up to date
  onUpdateRegionRef.current = onUpdateRegion;
  updatedRegionRef.current = updatedRegion;
  isPlayingRef.current = isPlaying;

  // Format time helper
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);



  // Initialize WaveSurfer - only once per audio file
  useEffect(() => {
    if (!containerRef.current || !audioData.file) return;

    let cancelled = false;
    const detachListeners = [];
    const addListener = (target, type, handler, options) => {
      target.addEventListener(type, handler, options);
      detachListeners.push(() => target.removeEventListener(type, handler, options));
    };

    // Clean up any existing instance
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
    
    // Native scrollbar - no cleanup needed
    
    // Clean up any existing region element
    if (regionElementRef.current) {
      regionElementRef.current.remove();
      regionElementRef.current = null;
    }

    // Create WaveSurfer instance without regions plugin
    const wavesurfer = WaveSurfer.create({
      container: containerRef.current,
      waveColor: 'rgba(139, 92, 246, 0.3)',
      progressColor: 'rgba(139, 92, 246, 0.6)',
      cursorColor: 'rgba(139, 92, 246, 0.8)',
      height: 300,
      normalize: true,
      barHeight: 0.8,
      barGap: 0,
      barRadius: 2,
      barWidth: 0,
      autoCenter: false,
      autoScroll: false,
      dragToSeek: false,
      hideScrollbar: false, // Show native scrollbar
      minPxPerSec: 1,
      interact: false, // Disable all interactions with the waveform
      scrollParent: true, // Enable scroll parent for horizontal scrolling
      plugins: [
        TimelinePlugin.create({
          height: 20,
          insertPosition: 'beforebegin',
          style: { color: '#2D5B88' }
        }),
        TimelinePlugin.create({
          height: 20,
          style: { color: '#6A3274' }
        })
      ]
    });

    wavesurferRef.current = wavesurfer;

    // Handle ready event
    wavesurfer.on('ready', () => {
      if (cancelled) return;
      setIsLoaded(true);
      const audioDuration = wavesurfer.getDuration();
      setDuration(audioDuration);

      // Create custom region element
      const savedRegion = updatedRegionRef.current || { start: 0, end: Math.min(REGION_DURATION, audioDuration) };
      const regionStart = Math.max(0, Math.min(savedRegion.start, audioDuration - REGION_DURATION));
      const regionEnd = Math.min(regionStart + REGION_DURATION, audioDuration);

      // Create region element
      const regionElement = document.createElement('div');
      regionElement.className = 'custom-region';
      regionElement.style.cssText = `
        position: absolute;
        top: 0;
        height: 100%;
        background: rgba(139, 92, 246, 0.3);
        border: 2px solid rgba(139, 92, 246, 0.8);
        cursor: move;
        z-index: 10;
        pointer-events: auto;
        touch-action: none;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
      `;

      // Add to container first
      const regionWrapper = wavesurfer.getWrapper();
      regionWrapper.style.position = 'relative';
      regionWrapper.appendChild(regionElement);
      regionElementRef.current = regionElement;
      
      // Position the region
      const pxPerSec = wavesurfer.getDuration() / wavesurfer.getWrapper().scrollWidth;
      const startPx = regionStart / pxPerSec;
      const widthPx = REGION_DURATION / pxPerSec;
      
      regionElement.style.left = `${startPx}px`;
      regionElement.style.width = `${widthPx}px`;

      // Add drag functionality (mouse + touch support)
      let isDragging = false;
      let dragStartX = 0;
      let dragStartRegionLeft = 0;
      let animationFrameId = null;

      // Helper function to get position relative to wrapper
      const getPositionRelativeToWrapper = (e) => {
        const wrapper = wavesurfer.getWrapper();
        const wrapperRect = wrapper.getBoundingClientRect();
        let clientX = 0;
        
        if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
        } else if (e.changedTouches && e.changedTouches.length > 0) {
          clientX = e.changedTouches[0].clientX;
        } else {
          clientX = e.clientX || 0;
        }
        
        // Return position relative to wrapper (accounting for scroll)
        return clientX - wrapperRect.left + wrapper.scrollLeft;
      };

      // Helper function to handle drag start
      const handleDragStart = (e) => {
        isDragging = true;
        dragStartX = getPositionRelativeToWrapper(e);
        dragStartRegionLeft = parseFloat(regionElement.style.left);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Prevent text selection during drag
        document.body.style.userSelect = 'none';
        document.body.style.webkitUserSelect = 'none';
      };

      // Helper function to handle drag move
      const handleDragMove = (e) => {
        if (!isDragging) return;
        
        // Prevent default scrolling and other touch behaviors
        e.preventDefault();
        e.stopPropagation();
        
        // Cancel previous animation frame
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        
        // Use requestAnimationFrame for smooth visual updates only
        animationFrameId = requestAnimationFrame(() => {
          const currentX = getPositionRelativeToWrapper(e);
          const deltaX = currentX - dragStartX;
          const newLeft = dragStartRegionLeft + deltaX;
          
          // Constrain to valid bounds
          const wrapper = wavesurfer.getWrapper();
          const maxLeft = wrapper.scrollWidth - parseFloat(regionElement.style.width);
          const constrainedLeft = Math.max(0, Math.min(newLeft, maxLeft));
          
          // Only update visual position
          regionElement.style.left = `${constrainedLeft}px`;
        });
      };

      // Helper function to handle drag end
      const handleDragEnd = (e) => {
        if (!isDragging) return;
        isDragging = false;

        document.body.style.userSelect = '';
        document.body.style.webkitUserSelect = '';

        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
          animationFrameId = null;
        }

        const pxPerSec = wavesurfer.getDuration() / wavesurfer.getWrapper().scrollWidth;
        const currentLeft = parseFloat(regionElement.style.left);
        const newStart = currentLeft * pxPerSec;
        const newEnd = newStart + REGION_DURATION;

        const regionData = { start: newStart, end: newEnd };
        setRegion(regionData);
        onUpdateRegionRef.current(regionData);

        if (isPlayingRef.current && wavesurferRef.current) {
          wavesurferRef.current.setTime(newStart);
        }

        // Only cancel the synthetic click for the drag that just ended.
        // A document-level preventDefault on every touchend (the previous
        // behaviour) also killed taps on the export modal after leaving this page.
        if (e.type === 'touchend' || e.type === 'touchcancel') {
          e.preventDefault();
        }
      };

      addListener(regionElement, 'mousedown', handleDragStart);
      addListener(document, 'mousemove', handleDragMove);
      addListener(document, 'mouseup', handleDragEnd);
      addListener(regionElement, 'touchstart', handleDragStart, { passive: false, capture: true });
      addListener(document, 'touchmove', handleDragMove, { passive: false, capture: true });
      addListener(document, 'touchend', handleDragEnd, { passive: false, capture: true });
      addListener(document, 'touchcancel', handleDragEnd, { passive: false, capture: true });

      // Update state and context
      const regionData = { start: regionStart, end: regionEnd };
      setRegion(regionData);
      onUpdateRegionRef.current(regionData);
    });

    // Handle play/pause events
    wavesurfer.on('play', () => setIsPlaying(true));
    wavesurfer.on('pause', () => setIsPlaying(false));
    wavesurfer.on('finish', () => setIsPlaying(false));

    // Load audio file
    wavesurfer.loadBlob(audioData.file);

    return () => {
      cancelled = true;
      detachListeners.forEach((off) => off());
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      if (regionElementRef.current) {
        regionElementRef.current.remove();
        regionElementRef.current = null;
      }
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
        wavesurferRef.current = null;
      }
    };
  }, [audioData.file]); // Only re-initialize when audio file changes

  // Handle region updates from context (when returning from another page)
  useEffect(() => {
    if (updatedRegionRef.current && regionElementRef.current && isLoaded && wavesurferRef.current) {
      const newStart = Math.max(0, updatedRegionRef.current.start);
      const newEnd = Math.min(duration, newStart + REGION_DURATION);
      
      // Update custom region position
      const wavesurfer = wavesurferRef.current;
      const wrapper = wavesurfer.getWrapper();
      const pxPerSec = wavesurfer.getDuration() / wrapper.scrollWidth;
      const startPx = newStart / pxPerSec;
      
      regionElementRef.current.style.left = `${startPx}px`;
      
      setRegion({ start: newStart, end: newEnd });
    }
  }, [isLoaded, duration]);

  // Handle zoom level changes
  useEffect(() => {
    if (!wavesurferRef.current || !isLoaded) return;

    const pxPerSec = Math.pow(2, zoomLevel) * 10;
    wavesurferRef.current.zoom(pxPerSec);
    
    // Update region position after zoom
    if (regionElementRef.current) {
      // Use a small delay to ensure WaveSurfer has updated its dimensions
      setTimeout(() => {
        const wavesurfer = wavesurferRef.current;
        const wrapper = wavesurfer.getWrapper();
        const scrollWidth = wrapper.scrollWidth;
        const duration = wavesurfer.getDuration();
        const pxPerSec = duration / scrollWidth;
        
        const startPx = region.start / pxPerSec;
        const widthPx = REGION_DURATION / pxPerSec;
        
        regionElementRef.current.style.left = `${startPx}px`;
        regionElementRef.current.style.width = `${widthPx}px`;
      }, 100);
    }
  }, [zoomLevel, isLoaded, region.start]);

  // Handle looping playback within region
  useEffect(() => {
    if (!wavesurferRef.current || !isLoaded) return;

    const handleAudioProcess = (currentTime) => {
      if (currentTime >= region.end) {
        // Loop back to region start instead of pausing
        wavesurferRef.current.setTime(region.start);
      }
    };

    wavesurferRef.current.on('audioprocess', handleAudioProcess);

    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.un('audioprocess', handleAudioProcess);
      }
    };
  }, [isLoaded, region.end, region.start]);

  // Play/Pause handler - always plays from region start and loops within region
  const handlePlayPause = useCallback(() => {
    if (!wavesurferRef.current || !isLoaded) return;

    if (isPlaying) {
      wavesurferRef.current.pause();
    } else {
      // Get the current region start value (use state directly, not from closure)
      const currentRegion = region;
      // Play from region start and loop within region
      wavesurferRef.current.setTime(currentRegion.start);
      wavesurferRef.current.play();
    }
  }, [isLoaded, isPlaying, region]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    if (!isLoaded || !wavesurferRef.current) return;
    
    const newZoom = Math.min(zoomLevel + 1, 10);
    setZoomLevel(newZoom);
  }, [zoomLevel, isLoaded]);

  const handleZoomOut = useCallback(() => {
    if (!isLoaded || !wavesurferRef.current) return;
    
    const newZoom = Math.max(zoomLevel - 1, -5);
    setZoomLevel(newZoom);
  }, [zoomLevel, isLoaded]);


  if (!audioData.file) {
    return (
      <div className="audio-editor">
        <div className="no-audio-message">
          <p>Please upload an audio file to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="audio-editor">
      {/* Header with duration info */}
      <div className="audio-editor-header">
        <div className="duration-info">
          <span className="duration-label">Selected 60s:</span>
          <span className="duration-value">
            {formatTime(region.start)} - {formatTime(region.end)} 
            ({formatTime(region.end - region.start)})
          </span>
        </div>
        {duration > 0 && (
          <div className="duration-info">
            <span className="duration-label">Total Duration:</span>
            <span className="duration-value">{formatTime(duration)}</span>
          </div>
        )}
      </div>

      {/* Waveform container - native scrollbar will be visible */}
      <div className="waveform-container" ref={containerRef} />

      {/* Controls */}
      <div className="audio-controls">
        {/* Playback controls */}
        <div className="playback-controls">
          <button 
            className="control-btn primary"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePlayPause();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handlePlayPause();
            }}
            disabled={!isLoaded}
            title={isPlaying ? "Pause" : "Play region (loops)"}
            type="button"
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            <span>{isPlaying ? 'Pause' : 'Play'}</span>
          </button>
        </div>

        {/* Zoom controls */}
        <div className="zoom-controls">
          <button 
            className="control-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleZoomOut();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleZoomOut();
            }}
            disabled={!isLoaded || zoomLevel <= -5}
            title="Zoom Out"
            type="button"
          >
            <FontAwesomeIcon icon={faSearchMinus} />
          </button>
          <button 
            className="control-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleZoomIn();
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleZoomIn();
            }}
            disabled={!isLoaded || zoomLevel >= 10}
            title="Zoom In"
            type="button"
          >
            <FontAwesomeIcon icon={faSearchPlus} />
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="audio-instructions">
        <p>
          <strong>Tip:</strong> Drag the highlighted region to select your 60-second clip.
        </p>
      </div>
    </div>
  );
};

export default AudioEditor;
